import React, { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import Layout from "../components/layout/Layout";
import { useI18n } from "@/i18n";

type LayoutResponse = {
  ok: boolean;
  layout?: { path?: unknown } | null;
};

type ProfileResponse = {
  ok?: boolean;
  user?: { default_start_page?: unknown } | null;
};

const resolveAllowedStartPage = (
  path: unknown,
  opts: { hasUA: boolean; hasADR: boolean; hasSF: boolean; isSuperAdmin: boolean },
): string => {
  if (path === '/welcome') return '/welcome';
  if (path === '/admin/dashboard' && opts.isSuperAdmin) return '/admin/dashboard';
  if (path === '/ua/dashboard' && opts.hasUA) return '/ua/dashboard';
  if (path === '/adr/dashboard' && opts.hasADR) return '/adr/dashboard';
  if (path === '/sf/dashboard' && opts.hasSF) return '/sf/dashboard';
  return '/welcome';
};

export default function IndexPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const { t } = useI18n();
  const [hasRedirected, setHasRedirected] = useState(false);

  const allowedModules = Array.isArray(session?.user?.allowedModules)
    ? session?.user?.allowedModules
    : [];
  const isSuperAdmin = Boolean(session?.user?.isSuperAdmin);
  const hasUA = allowedModules.includes("UA");
  const hasADR = allowedModules.includes("ADR");
  const hasSF =
    allowedModules.includes("SF") || allowedModules.includes("SettleFast");

  useEffect(() => {
    if (status === 'unauthenticated') {
      router.replace('/login');
      return;
    }

    if (status !== 'authenticated' || hasRedirected) {
      return;
    }

    let active = true;

    const runRedirect = async () => {
      let preferredPath: unknown = null;
      try {
        const profileResponse = await fetch('/api/profile/me', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const profileData = (await profileResponse.json().catch(() => null)) as
          | ProfileResponse
          | null;
        if (profileResponse.ok && profileData?.ok) {
          preferredPath = profileData.user?.default_start_page;
        }
      } catch {
        preferredPath = null;
      }

      if (preferredPath == null) {
        try {
          const response = await fetch('/api/ui/layout?layoutKey=start_page&scopeKey=', {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          const data = (await response.json().catch(() => null)) as LayoutResponse | null;
          if (response.ok && data?.ok && typeof data.layout?.path === 'string') {
            preferredPath = data.layout.path;
          }
        } catch {
          preferredPath = null;
        }
      }

      if (!active) return;
      const destination = resolveAllowedStartPage(preferredPath, {
        hasUA,
        hasADR,
        hasSF,
        isSuperAdmin,
      });
      setHasRedirected(true);
      router.replace(destination);
    };

    void runRedirect();

    return () => {
      active = false;
    };
  }, [hasADR, hasRedirected, hasSF, hasUA, isSuperAdmin, router, status]);

  if (status === "loading" || (status === 'authenticated' && !hasRedirected)) {
    return <div role="status" aria-label={t("common.state.loading")} />;
  }

  return <Layout>{null}</Layout>;
}
